// End-to-end crypto for one-time secret sharing. Standalone + WASM-free (native WebCrypto only) so the
// public recipient page stays tiny and never loads the vault crypto core. The decryption key lives in
// the URL fragment and never reaches the server — the server only ever holds ciphertext.
//
// Scheme: random K (32B) + salt (16B) in the URL fragment. AES-256-GCM key = HKDF-SHA256(salt, ikm),
// where ikm = K, or K || PBKDF2-SHA256(passphrase, salt, 210k) when a passphrase is set (optional
// second factor shared out-of-band). Payload = nonce(12) || AES-GCM(key, nonce, JSON).

export interface ShareField {
  label: string;
  value: string;
}
export interface SharePayload {
  v: 1;
  fields: ShareField[];
  note?: string;
}

const HKDF_INFO = new TextEncoder().encode("vault-otshare-v1");
const PBKDF2_ITERS = 210_000;

// --- base64url (fragment) ---
export function b64urlEncode(b: Uint8Array): string {
  let s = "";
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
export function b64urlDecode(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
// --- standard base64 (ciphertext on the wire; the backend uses std base64) ---
function b64Encode(b: Uint8Array): string {
  let s = "";
  for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
  return btoa(s);
}
function b64Decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

async function pbkdf2(passphrase: string, salt: Uint8Array): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: salt as BufferSource, iterations: PBKDF2_ITERS, hash: "SHA-256" },
    base,
    256,
  );
  return new Uint8Array(bits);
}

// Derive the AES-GCM key from the fragment key K + salt (+ passphrase).
async function deriveKey(k: Uint8Array, salt: Uint8Array, passphrase?: string): Promise<CryptoKey> {
  let ikm = k;
  if (passphrase) {
    ikm = concat(k, await pbkdf2(passphrase, salt));
  }
  const hkdfKey = await crypto.subtle.importKey("raw", ikm as BufferSource, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: salt as BufferSource, info: HKDF_INFO as BufferSource },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export interface EncryptedShare {
  /** Standard base64 of nonce||ciphertext — sent to the server, stored as-is. */
  ciphertextB64: string;
  /** base64url fragment parts: k = random key, s = salt. */
  k: string;
  s: string;
  passphraseRequired: boolean;
}

/** Encrypt a share payload. If `passphrase` is set it becomes a required second factor. */
export async function encryptShare(payload: SharePayload, passphrase?: string): Promise<EncryptedShare> {
  const k = crypto.getRandomValues(new Uint8Array(32));
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(k, salt, passphrase || undefined);
  const pt = new TextEncoder().encode(JSON.stringify(payload));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce as BufferSource }, key, pt));
  return {
    ciphertextB64: b64Encode(concat(nonce, ct)),
    k: b64urlEncode(k),
    s: b64urlEncode(salt),
    passphraseRequired: !!passphrase,
  };
}

/** Decrypt a revealed share. Throws if the passphrase is wrong or the payload is corrupt. */
export async function decryptShare(ciphertextB64: string, k: string, s: string, passphrase?: string): Promise<SharePayload> {
  const blob = b64Decode(ciphertextB64);
  if (blob.length < 13) throw new Error("corrupt payload");
  const nonce = blob.slice(0, 12);
  const ct = blob.slice(12);
  const key = await deriveKey(b64urlDecode(k), b64urlDecode(s), passphrase || undefined);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce as BufferSource }, key, ct as BufferSource);
  return JSON.parse(new TextDecoder().decode(pt)) as SharePayload;
}
